import { Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
  Alert,
  Chip,
  cn,
} from '@c2fo/liquidity';
import { CheckIcon, InfoCircleIcon, LockKeyholeIcon } from '@c2fo/liquidity/icons';
import ActivationProgress from '@/enterprise/components/InstantPay/ActivationProgress';
import BankConnectionCard from '@/enterprise/components/InstantPay/BankConnectionCard';
import BusinessVerificationCard from '@/enterprise/components/InstantPay/BusinessVerificationCard';
import CustomerRoutingCard from '@/enterprise/components/InstantPay/CustomerRoutingCard';
import PlaidModal from '@/enterprise/components/PlaidModal/PlaidModal';
import Skeleton from '@/enterprise/components/Skeleton';
import {
  ActivationStepState,
  CompanyStatusDocument,
  countCompleteSteps,
  deriveActivationSteps,
  hasAnyCompletedField,
  isBusinessVerificationComplete,
} from '@/enterprise/data/activationStatus';
import {
  isAllDivisionsOnboardingComplete,
  isBuyerRemittanceComplete,
  isInstantPayEnabledUsdMarket,
} from '@/enterprise/data/instantPayEnrollment';
import useCompanyStatus from '@/enterprise/data/useCompanyStatus';
import useGroupByBuyer from '@/enterprise/data/useGroupByBuyer';
import useRemittanceEnvelopes from '@/enterprise/data/useRemittanceEnvelopes';
import useTakerMarkets from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import useDataDog from '@/reporting/DataDog';
import ErrorBoundary from '@/shared/components/ErrorBoundary';
import { trackInstantPayOnboarding } from './trackInstantPayOnboarding';

const StepBadge = ({ status }: { status: ActivationStepState['status'] }) => {
  const { t } = useTranslation();

  if (status === 'complete') {
    return <Chip label={t('instantPay.onboarding.step.complete')} variant="filled" isRound type="primary" />;
  }

  if (status === 'locked') {
    return <Chip label={t('instantPay.onboarding.step.locked')} variant="outlined" isRound />;
  }

  return <Chip label={t('instantPay.onboarding.step.inProgress')} variant="filled" isRound type="warning" />;
};

const STEP_TITLES: Record<1 | 2 | 3, { title: string; description: string }> = {
  1: {
    title: 'instantPay.onboarding.step1.title',
    description: 'instantPay.onboarding.step1.description',
  },
  2: {
    title: 'instantPay.onboarding.step2.title',
    description: 'instantPay.onboarding.step2.description',
  },
  3: {
    title: 'instantPay.onboarding.step3.title',
    description: 'instantPay.onboarding.step3.description',
  },
};

const StepIcon = ({ step, status }: { step: 1 | 2 | 3; status: ActivationStepState['status'] }) => {
  if (status === 'complete') {
    return (
      <div className="flex size-10 shrink-0 items-center justify-center rounded-full border border-primary-500 bg-canvas-green">
        <CheckIcon className="size-6 fill-primary-500" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex size-10 shrink-0 items-center justify-center rounded-full bg-deepGray-100 text-xl font-semibold text-text-primary',
        {
          'bg-primary-500 text-white': status === 'in-progress',
        }
      )}
    >
      {step}
    </div>
  );
};

// Stable reference so `companyDocuments` doesn't change identity on every render while company
// status hasn't loaded yet (a fresh `[]` literal would otherwise unnecessarily bust the useMemo
// below on each re-render).
const NO_COMPANY_DOCUMENTS: CompanyStatusDocument[] = [];

// Isolated so a buyer's own query errors (e.g. a 403 fetching its signed document's metadata) are
// caught here rather than propagating past the whole step 3 list.
const CustomerRoutingCardError = ({ makerOrganizationUuid }: { makerOrganizationUuid: string }) => {
  const { t } = useTranslation();
  const { addError } = useDataDog();

  useEffect(() => {
    addError('CustomerRoutingCard - Load Error', { makerOrganizationUuid });
  }, [addError, makerOrganizationUuid]);

  return <Alert full type="error" variant="outlined" description={t('instantPay.onboarding.customerCard.loadError')} />;
};

// Isolated so failing to load the Instant Pay terms only affects step 1.
const BusinessVerificationCardError = () => {
  const { t } = useTranslation();
  const { addError } = useDataDog();

  useEffect(() => {
    addError('BusinessVerificationCard - Load Error');
  }, [addError]);

  return <Alert full type="error" variant="outlined" description={t('instantPay.onboarding.terms.loadError')} />;
};

// Isolated so an error loading the page's own required data (taker markets, company status,
// remittance envelopes) shows a local message instead of blanking the whole app around this page.
const InstantPayOnboardingError = () => {
  const { t } = useTranslation();
  const { addError } = useDataDog();

  useEffect(() => {
    addError('InstantPayOnboarding - Load Error');
  }, [addError]);

  return <Alert full type="error" variant="outlined" description={t('instantPay.onboarding.pageError')} />;
};

const InstantPayOnboardingContent = () => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const { data: takerMarkets } = useTakerMarkets((markets) =>
    markets.filter((market) => isInstantPayEnabledUsdMarket(market.instantPay, market.currency))
  );
  const buyers = useGroupByBuyer({ takerMarkets });
  const { data: companyStatus = [], isPending: isCompanyStatusPending } = useCompanyStatus();
  const company = companyStatus[0];
  const companyUuid = company?.companyUuid;
  const companyDocuments = company?.documents ?? NO_COMPANY_DOCUMENTS;
  const companyTaxId = company?.taxId ?? null;
  const companyBusinessAddress = company?.businessAddress ?? null;
  const bankAccount = company?.bankAccount ?? null;
  const hasPendingBankAccountVerification = bankAccount?.status === 'pending_manual_verification';
  const { data: remittanceEnvelopesByBuyer } = useRemittanceEnvelopes({ enabled: Boolean(companyUuid) });

  const completedRemittanceCount = buyers.filter((buyer) => isBuyerRemittanceComplete(buyer)).length;
  const allDivisionsOnboardingComplete = isAllDivisionsOnboardingComplete(takerMarkets ?? []);

  const businessVerificationInput = useMemo(
    () => ({
      hasAgreedToTerms: Boolean(companyUuid),
      hasSubmittedTin: Boolean(companyUuid),
      documents: companyDocuments,
      allDivisionsOnboardingComplete,
    }),
    [companyUuid, companyDocuments, allDivisionsOnboardingComplete]
  );

  const steps = useMemo(
    () =>
      deriveActivationSteps({
        businessVerification: businessVerificationInput,
        bankConnection: { bankAccount },
        paymentRouting: { totalCustomers: buyers.length, completedCustomers: completedRemittanceCount },
      }),
    [businessVerificationInput, bankAccount, buyers.length, completedRemittanceCount]
  );

  const completedStepCount = countCompleteSteps(steps);
  const showModifyNotice = hasAnyCompletedField(businessVerificationInput, steps);
  const businessVerificationAwaitingOnboarding =
    isBusinessVerificationComplete(businessVerificationInput) && !allDivisionsOnboardingComplete;

  // A manual expand overrides auto-advance only until the user clicks back to the active step.
  const firstIncompleteStepNumber = steps.find((step) => step.status !== 'complete')?.step;
  const autoExpandedStep = firstIncompleteStepNumber != null ? String(firstIncompleteStepNumber) : null;
  const [manualStep, setManualStep] = useState<string | null>(null);
  const expandedStep = manualStep ?? autoExpandedStep ?? '';
  const handleExpandedStepChange = (value: string) => {
    setManualStep(value === autoExpandedStep ? null : value);
  };

  const [plaidModalOpen, setPlaidModalOpen] = useState(false);
  const [plaidModalMode, setPlaidModalMode] = useState<'connect' | 'relink'>('connect');
  const [connectLoading, setConnectLoading] = useState(false);

  // useCompanyStatus isn't a suspense query (see its own comment for why), so before its first
  // fetch resolves `companyStatus` defaults to `[]` above — rendering every step as incomplete for
  // a flash before flipping to the real state. Show the same loading skeleton Suspense would until
  // that first fetch lands; a later background refetch (e.g. after the Plaid exchange) keeps
  // `isPending` false and `companyStatus` on its previous value via `keepPreviousData`, so this
  // only fires once, on initial load.
  if (isCompanyStatusPending) {
    return <InstantPayOnboardingLoadingSkeleton />;
  }

  return (
    <>
      <div className="flex flex-col gap-6">
        <ActivationProgress completedSteps={completedStepCount} />

        {showModifyNotice && (
          <Alert
            full
            variant="standard"
            title={t('instantPay.onboarding.modifyNoticeTitle')}
            description={t('instantPay.onboarding.modifyNoticeDescription')}
          />
        )}

        <Accordion type="single" collapsible value={expandedStep} onValueChange={handleExpandedStepChange}>
          {steps.map((step) => {
            const { title, description } = STEP_TITLES[step.step];
            const isLocked = step.status === 'locked';

            return (
              <AccordionItem
                key={step.step}
                value={String(step.step)}
                disabled={isLocked}
                className={cn(
                  'mb-6 overflow-hidden rounded-xl border border-transparent bg-white shadow-sm last:mb-0',
                  { 'border-primary-500': step.status === 'in-progress' }
                )}
              >
                <AccordionHeader className="flex-col items-stretch border-none p-0">
                  <div className="flex flex-col gap-4 p-6">
                    <div
                      className={cn('flex items-center justify-between gap-4', {
                        'flex-col items-start md:flex-row md:items-center': step.step === 2,
                      })}
                    >
                      <div className="flex min-w-0 gap-4">
                        <StepIcon step={step.step} status={step.status} />
                        <div className="flex flex-col gap-1">
                          <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
                            <span className="text-xl font-bold text-text-primary">{t(title)}</span>
                            <StepBadge status={step.status} />
                          </div>
                          <span className="text-text-secondary">{t(description)}</span>
                          {isLocked && (
                            <span className="flex items-center gap-1 text-xs italic text-text-secondary">
                              <LockKeyholeIcon className="size-4 shrink-0 fill-text-secondary" />
                              {step.step === 3
                                ? t('instantPay.onboarding.step.lockedNoticeStep3')
                                : t('instantPay.onboarding.step.lockedNotice', { step: step.step - 1 })}
                            </span>
                          )}
                        </div>
                      </div>
                      {!isLocked &&
                        (step.step === 2 ? (
                          companyUuid && (
                            <BankConnectionCard
                              bankAccount={bankAccount}
                              connectLoading={connectLoading}
                              onConnect={() => {
                                trackInstantPayOnboarding(trackEnterpriseEvent, { step: 2, action: 'started' });
                                setPlaidModalMode('connect');
                                setPlaidModalOpen(true);
                              }}
                              onFinishVerification={() => {
                                setPlaidModalMode('relink');
                                setPlaidModalOpen(true);
                              }}
                            />
                          )
                        ) : step.step === 3 || step.status !== 'complete' ? null : (
                          <AccordionTrigger />
                        ))}
                    </div>
                    {!isLocked && step.status !== 'complete' && (
                      <div className="flex items-start gap-2.5 rounded-md border border-info-800 bg-info-50 px-4 py-1.5 text-info-900">
                        <InfoCircleIcon className="mt-[11px] size-4 shrink-0" />
                        <div className="flex flex-1 flex-col gap-1 py-2">
                          <p className="text-base font-semibold">
                            {t(`instantPay.onboarding.step${step.step}.alertTitle`)}
                          </p>
                          <p className="text-sm font-normal">
                            {t(`instantPay.onboarding.step${step.step}.alertDescription`)}
                          </p>
                        </div>
                      </div>
                    )}
                    {step.step === 2 && hasPendingBankAccountVerification && (
                      <p className="w-full text-center text-sm italic text-warning-900">
                        <span className="font-medium">
                          {t('instantPay.onboarding.connectBank.validationInProgressTitle')}
                        </span>
                        {' - '}
                        <span className="font-normal">
                          {t('instantPay.onboarding.connectBank.validationInProgressDescription')}
                        </span>
                      </p>
                    )}
                  </div>
                </AccordionHeader>
                {!isLocked && step.step === 1 && (
                  <AccordionContent className="px-6 pb-6">
                    <div className="flex flex-col gap-4">
                      <ErrorBoundary fallback={<BusinessVerificationCardError />}>
                        <Suspense fallback={<Skeleton height="h-52" />}>
                          <BusinessVerificationCard
                            hasSubmittedTin={Boolean(companyUuid)}
                            documents={companyDocuments}
                            taxId={companyTaxId}
                            businessAddress={companyBusinessAddress}
                          />
                        </Suspense>
                      </ErrorBoundary>
                      {businessVerificationAwaitingOnboarding && (
                        <p className="w-full text-center text-sm italic text-warning-900">
                          <span className="font-medium">
                            {t('instantPay.onboarding.terms.validationInProgressTitle')}
                          </span>
                          {' - '}
                          <span className="font-normal">
                            {t('instantPay.onboarding.terms.validationInProgressDescription')}
                          </span>
                        </p>
                      )}
                    </div>
                  </AccordionContent>
                )}
                {/* Never wrapped in AccordionContent — step 3 must stay visible even once every
                    buyer card is complete and it's no longer the accordion's controlled-open value. */}
                {!isLocked && step.step === 3 && (
                  <div className="flex flex-col gap-6 px-6 pb-4">
                    {buyers.map((buyer) => (
                      <ErrorBoundary
                        key={buyer.makerOrganizationUuid}
                        fallback={<CustomerRoutingCardError makerOrganizationUuid={buyer.makerOrganizationUuid} />}
                      >
                        <CustomerRoutingCard
                          buyer={buyer}
                          envelope={remittanceEnvelopesByBuyer?.get(buyer.makerOrganizationUuid)}
                          isFundingConfirmed={isBuyerRemittanceComplete(buyer)}
                        />
                      </ErrorBoundary>
                    ))}
                  </div>
                )}
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>

      {companyUuid && (
        <PlaidModal
          open={plaidModalOpen}
          companyUuid={companyUuid}
          mode={plaidModalMode}
          bankAccountUuid={
            plaidModalMode === 'relink' && hasPendingBankAccountVerification ? bankAccount?.uuid : undefined
          }
          onConnected={() => trackInstantPayOnboarding(trackEnterpriseEvent, { step: 2, action: 'completed' })}
          onClose={() => setPlaidModalOpen(false)}
          onLoadingChange={setConnectLoading}
        />
      )}
    </>
  );
};

const InstantPayOnboardingLoadingSkeleton = () => (
  <div className="space-y-6">
    <Skeleton height="h-52" />
    <Skeleton height="h-52" />
    <Skeleton height="h-52" />
  </div>
);

const InstantPayOnboarding = () => {
  const { t } = useTranslation();

  return (
    <div className="space-y-9">
      <div className="font-serif text-4xl text-text-primary">{t('instantPay.onboarding.pageTitle')}</div>

      <ErrorBoundary fallback={<InstantPayOnboardingError />}>
        <Suspense fallback={<InstantPayOnboardingLoadingSkeleton />}>
          <InstantPayOnboardingContent />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
};

export default InstantPayOnboarding;
